import { useEffect, useMemo, useState } from "react";
import { Bug, Lightbulb } from "lucide-react";
import api from "@/services/api";
import FeedbackModal from "@/components/FeedbackModal";

const SECTION_TITLE = {
  fontSize: '13px', fontWeight: 900, color: '#fff',
  letterSpacing: '0.04em', textTransform: 'uppercase', fontStyle: 'italic',
};

// ----------------------------------------------------------------------
// Pulls the user's completed matches once and derives:
//   - lastFive  : array of "W" | "D" | "L" (most recent last)
//   - evolution : [{ idx, rating }] starting at base 5.0, updated per match
// ----------------------------------------------------------------------
function useMatchHistory(userId) {
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    let alive = true;
    api.getMatches("completed").then((all) => {
      if (!alive || !userId) return;
      const mine = (all || [])
        .filter((m) => (m.players || []).some((p) =>
          (typeof p === "string" ? p : p.id) === userId,
        ))
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
      setMatches(mine);
    }).catch(() => {});
    return () => { alive = false; };
  }, [userId]);
  return matches;
}

function resultFor(m, userId) {
  const inA = (m.team_a || []).includes(userId);
  const inB = (m.team_b || []).includes(userId);
  if (!inA && !inB) return "D";
  const sa = m.score_a ?? 0, sb = m.score_b ?? 0;
  if (sa === sb) return "D";
  const won = (inA && sa > sb) || (inB && sb > sa);
  return won ? "W" : "L";
}

const COLOR = { W: "#22c55e", D: "#eab308", L: "#ef4444" };

export function FormeRecente({ userId }) {
  const matches = useMatchHistory(userId);
  const lastFive = useMemo(() => {
    return matches.slice(-5).map((m) => resultFor(m, userId));
  }, [matches, userId]);
  const slots = [...Array(5)].map((_, i) => {
    // align right: oldest filled-from-left when < 5 matches.
    const r = lastFive[i];
    return r;
  });
  const empty = lastFive.length === 0;
  return (
    <section data-testid="forme-recente" style={{ padding: '0 16px 20px 16px' }}>
      <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '20px' }}>
        <h3 className="font-heading italic uppercase" style={{ fontSize: '13px', fontWeight: 900, color: '#fff', letterSpacing: '0.04em', marginBottom: '14px' }}>Forme recente</h3>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 10, flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {slots.map((r, i) => (
              <span
                key={i}
                data-testid={`forme-slot-${i}`}
                style={{
                  width: 26, height: 26, borderRadius: 999,
                  background: r ? COLOR[r] : '#27272a',
                  border: r ? 'none' : '1px solid #3f3f46',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: r ? '#fff' : 'transparent',
                  fontSize: 11, fontWeight: 800,
                }}
              >
                {r || ''}
              </span>
            ))}
          </div>
          {empty && (
            <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
              En attente de vos resultats
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// Linear SVG performance chart. Starts at 5.0, +0.2 W / -0.15 L / +0 D, clamp [0,10].
export function EvolutionPerformance({ userId }) {
  const matches = useMatchHistory(userId);
  const series = useMemo(() => {
    let rating = 5.0;
    const points = [{ x: 0, rating }];
    matches.forEach((m, idx) => {
      const r = resultFor(m, userId);
      rating += r === "W" ? 0.2 : r === "L" ? -0.15 : 0;
      rating = Math.max(0, Math.min(10, rating));
      points.push({ x: idx + 1, rating });
    });
    return points;
  }, [matches, userId]);

  const W = 320, H = 100, P = 8;
  const xs = series.length - 1 || 1;
  const path = series.map((p, i) => {
    const x = P + (p.x / xs) * (W - P * 2);
    const y = H - P - ((p.rating / 10) * (H - P * 2));
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <section data-testid="evolution-performance" style={{ padding: '0 16px 20px 16px' }}>
      <h3 style={{ ...SECTION_TITLE, marginBottom: 10 }}>Evolution de performance</h3>
      <div style={{
        background: '#18181b', border: '1px solid #27272a', borderRadius: 12,
        padding: 14,
      }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: '100%', height: 100, display: 'block' }}
          data-testid="performance-svg"
        >
          {/* Baseline (5.0) */}
          <line x1={P} y1={H/2} x2={W-P} y2={H/2}
                stroke="#3f3f46" strokeDasharray="3 4" strokeWidth="1" />
          {/* Area fill */}
          <path d={`${path} L${W-P},${H-P} L${P},${H-P} Z`}
                fill="rgba(34,197,94,0.15)" />
          {/* Line */}
          <path d={path} fill="none" stroke="#22c55e" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
          {/* Last point */}
          {series.length > 0 && (() => {
            const last = series[series.length - 1];
            const x = P + (last.x / xs) * (W - P * 2);
            const y = H - P - ((last.rating / 10) * (H - P * 2));
            return <circle cx={x} cy={y} r="3" fill="#22c55e" stroke="#000" strokeWidth="1.5" />;
          })()}
        </svg>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 10, color: '#6b7280', marginTop: 4,
        }}>
          <span>Note de depart 5.0</span>
          <span>Actuelle {(series[series.length-1]?.rating || 5).toFixed(1)}</span>
        </div>
      </div>
    </section>
  );
}

export function AideAmeliorations() {
  const [modal, setModal] = useState(null); // null | "bug" | "idea"
  const Row = ({ Icon, label, testid, type }) => (
    <button
      data-testid={testid}
      onClick={() => setModal(type)}
      className="hover:bg-zinc-800/50 transition-colors"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        padding: '14px 14px', borderRadius: 10,
        background: '#18181b', border: '1px solid #27272a',
        color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <Icon size={18} color="#22c55e" />
      <span>{label}</span>
    </button>
  );
  return (
    <section data-testid="aide-section" style={{ padding: '0 16px 32px 16px' }}>
      <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '20px' }}>
        <h3 className="font-heading italic uppercase" style={{ fontSize: '13px', fontWeight: 900, color: '#fff', letterSpacing: '0.04em', marginBottom: '14px' }}>Aide & ameliorations</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Row Icon={Bug} label="Signaler un bug" testid="aide-bug" type="bug" />
          <Row Icon={Lightbulb} label="Donner mon avis / une idee" testid="aide-idea" type="idea" />
        </div>
      </div>
      <FeedbackModal open={modal !== null} type={modal} onClose={() => setModal(null)} />
    </section>
  );
}
